import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { from, Observable, Subject, throwError } from 'rxjs';
import { catchError, concatMap, takeUntil, tap, toArray } from 'rxjs/operators';

import { DistrictCoefficient, DistrictNames } from '../../sectors';
import { SectorsService } from '../../sectors.service';

interface DistrictCoefficientRow {
  district: DistrictNames;
  record: DistrictCoefficient | null;
  control: UntypedFormControl;
  savedCoefficient: string;
  loading: boolean;
  loadError: string | null;
  saveError: string | null;
}

@Component({
  selector: 'app-district-coefficients',
  templateUrl: './district-coefficients.component.html',
  styleUrls: ['./district-coefficients.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DistrictCoefficientsComponent implements OnChanges, OnDestroy {
  @Input() districts: DistrictNames[] = [];
  public rows: DistrictCoefficientRow[] = [];
  public saving = false;
  public coefficientOptions = Array.from({ length: 16 }, (_, index) => {
    const coefficient = (index + 5) / 10;
    return { value: coefficient.toFixed(4), label: coefficient.toFixed(2).replace('.', ',') };
  });
  private rowsByDistrict = new Map<number, DistrictCoefficientRow>();
  private destroyed$ = new Subject<void>();

  constructor(private sectorsService: SectorsService) {}

  get invalid(): boolean {
    return this.rows.some(row => row.loading || !!row.loadError || row.control.invalid);
  }

  ngOnChanges(): void {
    const selected = new Map((this.districts || []).map(district => [Number(district.d_id), district]));
    this.rows = Array.from(selected, ([districtId, district]) => {
      let row = this.rowsByDistrict.get(districtId);
      if (!row) {
        row = {
          district,
          record: null,
          control: new UntypedFormControl('1.0000'),
          savedCoefficient: '1.0000',
          loading: true,
          loadError: null,
          saveError: null,
        };
        // Existing API values can use a finer step. Preserve them until the user selects a new value.
        row.control.setValidators([
          Validators.required,
          control => {
            const allowed = control.value === row.savedCoefficient ||
              this.coefficientOptions.some(option => option.value === control.value);
            return allowed ? null : { coefficient: true };
          },
        ]);
        this.rowsByDistrict.set(districtId, row);
        this.loadCoefficient(row);
      }
      row.district = district;
      return row;
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public hasCustomCoefficient(row: DistrictCoefficientRow): boolean {
    return !this.coefficientOptions.some(option => option.value === row.savedCoefficient);
  }

  public trackDistrict(index: number, row: DistrictCoefficientRow): number {
    return Number(row.district.d_id);
  }

  public loadCoefficient(row: DistrictCoefficientRow): void {
    row.loading = true;
    row.loadError = null;
    row.control.disable();
    this.sectorsService.getDistrictCoefficients(Number(row.district.d_id)).pipe(takeUntil(this.destroyed$)).subscribe({
      next: response => {
        const records = Array.isArray(response.data) ? response.data : response.data?.results || [];
        const record = records.find(item => Number(item.district_id) === Number(row.district.d_id)) || null;
        this.setLoadedCoefficient(row, record);
      },
      error: error => {
        if (error.status === 404) {
          this.setLoadedCoefficient(row, null);
          return;
        }
        row.loading = false;
        row.loadError = this.errorMessage(error, 'Не удалось загрузить коэффициент района.');
      },
    });
  }

  public save(): Observable<unknown> {
    if (this.invalid || this.saving) {
      return throwError(() => new Error('Дождитесь загрузки коэффициентов и исправьте ошибки.'));
    }

    const changedRows = this.rows.filter(row => !row.record || row.control.value !== row.savedCoefficient);
    this.rows.forEach(row => {
      row.saveError = null;
      row.control.disable();
    });
    this.saving = true;

    // Keep successful writes in the draft so retrying a partial failure does not create duplicates.
    return from(changedRows).pipe(
      concatMap(row => {
        const coefficient = Number(row.control.value).toFixed(4);
        const request = row.record
          ? this.sectorsService.updateDistrictCoefficient(row.record.id, coefficient)
          : this.sectorsService.createDistrictCoefficient(Number(row.district.d_id), coefficient);

        return request.pipe(
          tap(response => {
            row.record = response.data;
            row.savedCoefficient = Number(response.data.coefficient).toFixed(4);
            row.control.setValue(row.savedCoefficient);
          }),
          catchError(error => {
            row.saveError = this.errorMessage(error, 'Не удалось сохранить коэффициент района.');
            return throwError(() => new Error(`${row.district.district_name}: ${row.saveError}`));
          })
        );
      }),
      toArray(),
      takeUntil(this.destroyed$),
    );
  }

  public finishSaving(): void {
    this.saving = false;
    this.rows.forEach(row => {
      if (!row.loading && !row.loadError) {
        row.control.enable();
      }
    });
  }

  private setLoadedCoefficient(row: DistrictCoefficientRow, record: DistrictCoefficient | null): void {
    row.record = record;
    row.savedCoefficient = record ? Number(record.coefficient).toFixed(4) : '1.0000';
    row.control.setValue(row.savedCoefficient);
    row.control.enable();
    row.loading = false;
  }

  private errorMessage(error: any, fallback: string): string {
    if (error.status === 403) {
      return 'Недостаточно прав для работы с коэффициентами районов.';
    }
    if (error.status === 404) {
      return 'Запись коэффициента района отсутствует. Обновите страницу.';
    }
    const data = error.error && error.error.data;
    const errors = data && (data.coefficient || data.district_id || data.non_field_errors);
    return (Array.isArray(errors) ? errors.join(' ') : errors) || error.error?.metadata?.message || fallback;
  }
}
